import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Anchor,
  Badge,
  ComboboxItem,
  Group,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { useQueries } from "@tanstack/react-query";

import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";
import { TruncatedCell } from "@components/Table/TruncatedCell";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";
import { getChannelPresence } from "@utils/rtmApi";
import { truncateWithEllipsis } from "@utils/truncateText";

import { ROLES } from "@constants/index";

import { useAuth } from "@contexts/auth";

import { ChannelPresenceUser } from "@customTypes/rtm";
import { UnifiedScheduleItem, UnifiedStatus } from "@customTypes/schedule";

import { ScheduleTableControls } from "./ScheduleTableControls";


const statusColors: Record<UnifiedStatus, { c: string; bg: string }> = {
  pending: { c: "yellow.9", bg: "yellow.0" },
  booked: { c: "blue.9", bg: "blue.0" },
  delivered: { c: "gray.9", bg: "gray.1" },
  cancelled: { c: "red.9", bg: "red.0" },
  rejected: { c: "red.9", bg: "red.0" },
  follow_up: { c: "orange.9", bg: "orange.0" },
  closed: { c: "green.9", bg: "green.0" },
};

const billStatusColors = new Map([
  ["pending", { c: "yellow.9", bg: "yellow.0" }],
  ["accepted", { c: "accent.9", bg: "accent.0" }],
  ["paid", { c: "green.9", bg: "green.0" }],
]);

const getCallStatusColor = (status?: string) => {
  if (status === "started") return "green";
  if (["complete", "completed", "ended"].includes(status ?? "")) return "gray";
  return "yellow";
};

const getCallStatusKey = (status?: string) => {
  if (status === "started") return "call-status-started";
  if (["complete", "completed", "ended"].includes(status ?? "")) {
    return "call-status-ended";
  }
  return "call-status-not-started";
};

const isTeleconsultationScheduleItem = (item: UnifiedScheduleItem) => {
  const category = item.category?.toLowerCase() ?? "";
  const service = item.service?.toLowerCase() ?? "";

  return (
    category === "teleconsultation" || service.includes("teleconsultation")
  );
};

const hasPresenceRole = (users: ChannelPresenceUser[], expectedRole: string) => {
  return users.some(user => {
    const primaryRole = user.metadata?.role?.toLowerCase();
    const roles = user.metadata?.roles?.map(role => role.toLowerCase()) ?? [];

    return primaryRole === expectedRole || roles.includes(expectedRole);
  });
};

interface ScheduleTableProps {
  data: UnifiedScheduleItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  isRefetching: boolean;
  pagination: any;
  onPageChange: (page: number) => void;
  doctors: ComboboxItem[];
}

export const ScheduleTable = ({
  data,
  isLoading,
  isError,
  isSuccess,
  isRefetching,
  pagination,
  onPageChange,
  doctors,
}: ScheduleTableProps) => {
  const { t, i18n } = useTranslation();
  const { roles, can } = useAuth();
  const showCallMonitoring = can("communication.call");

  const callPresenceQueries = useQueries({
    queries:
      data
        ?.filter(
          item => isTeleconsultationScheduleItem(item) && !!item.callChannelId
        )
        .map(item => ({
          queryKey: ["rtmPresence", item.callChannelId],
          queryFn: () => getChannelPresence(item.callChannelId as string),
          enabled: showCallMonitoring,
          refetchInterval: 1000 * 10,
          staleTime: 1000 * 5,
        })) ?? [],
  });

  const presenceByChannel = useMemo(() => {
    const map = new Map<string, ChannelPresenceUser[]>();

    callPresenceQueries.forEach(query => {
      if (query.data?.channel_id) {
        map.set(query.data.channel_id, query.data.users);
      }
    });

    return map;
  }, [callPresenceQueries]);

  const scheduleRows = useMemo(() => {
    return data?.map(item => {
      const channelUsers = item.callChannelId
        ? presenceByChannel.get(item.callChannelId) ?? []
        : [];
      const hasNonPatientParticipant = channelUsers.some(
        user => user.user_id !== item.patientId
      );
      const doctorOnline = item.doctorId
        ? channelUsers.some(user => user.user_id === item.doctorId) ||
          hasPresenceRole(channelUsers, ROLES.DOCTOR) ||
          hasNonPatientParticipant
        : hasPresenceRole(channelUsers, ROLES.DOCTOR) ||
          hasNonPatientParticipant;
      const patientOnline = item.patientId
        ? channelUsers.some(user => user.user_id === item.patientId) ||
          hasPresenceRole(channelUsers, "patient")
        : hasPresenceRole(channelUsers, "patient");

      return (
      <Table.Tr key={item.id} pos="relative">
        <Table.Td>
          <Stack gap={0}>
            {item.patientId ? (
              <Anchor
                fz={14}
                component={Link}
                to={`/patients/${item.patientId}/profile`}
                c="accent"
                fw={400}>
                {truncateWithEllipsis(item.patientName, 25)}
              </Anchor>
            ) : (
              <Text fz={14} lh={1.5}>
                {truncateWithEllipsis(item.patientName, 25)}
              </Text>
            )}
            <Text fz={13} c="dimmed" lh={1.5}>
              {item.phoneNumber || "N/A"}
            </Text>
          </Stack>
        </Table.Td>

        <Table.Td>
          <Stack gap={0}>
            <Text fz={14} lh={1.5}>
              {formatDateToString(item.date)}
            </Text>
            <Text fz={13} c="dimmed" lh={1.5}>
              {item.date.toLocaleTimeString(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
          </Stack>
        </Table.Td>

        <Table.Td>
          <Stack gap={0}>
            <Text fz={14} lh={1.5}>
              {item.service || "N/A"}
            </Text>
            <Text fz={12} c="dimmed" lh={1.5} tt="capitalize">
              {t(item.category || "")}
            </Text>
          </Stack>
        </Table.Td>

        <Table.Td>
          <Badge
            tt="capitalize"
            c={statusColors[item.status]?.c}
            bg={statusColors[item.status]?.bg}>
            {t(item.status.replace("_", "-"))}
          </Badge>
        </Table.Td>

        <Table.Td>
          {item.billStatus ? (
            <Badge
              fz={12}
              size="md"
              c={billStatusColors.get(item.billStatus)?.c}
              bg={billStatusColors.get(item.billStatus)?.bg}>
              {t(item.billStatus)}
            </Badge>
          ) : (
            "N/A"
          )}
        </Table.Td>

        <Table.Td>
          <TruncatedCell value={item.doctorName || t("unassigned")} />
        </Table.Td>

        {showCallMonitoring && (
          <Table.Td>
            {isTeleconsultationScheduleItem(item) ? (
              <Stack gap={6}>
                <Badge
                  w="max-content"
                  variant="light"
                  color={getCallStatusColor(item.callStatus)}>
                  {t(getCallStatusKey(item.callStatus))}
                </Badge>
                {item.callChannelId ? (
                  <Group gap={6}>
                    <Badge
                      variant="dot"
                      color={doctorOnline ? "green" : "gray"}>
                      {t("doctor")}
                    </Badge>
                    <Badge
                      variant="dot"
                      color={patientOnline ? "green" : "gray"}>
                      {t("patient")}
                    </Badge>
                  </Group>
                ) : (
                  <Text fz={12} c="dimmed">
                    {t("no-call-channel")}
                  </Text>
                )}
              </Stack>
            ) : (
              <Text fz={12} c="dimmed">
                N/A
              </Text>
            )}
          </Table.Td>
        )}

        <Table.Td>
          <ScheduleTableControls row={item} doctors={doctors} />
        </Table.Td>
      </Table.Tr>
      );
    });
  }, [data, doctors, i18n.language, presenceByChannel, showCallMonitoring, t]);

  const columnCount = showCallMonitoring ? 8 : 7;

  return (
    <>
      <Table
        withRowBorders
        mt="2rem"
        miw={1200}
        withTableBorder
        highlightOnHover
        highlightOnHoverColor="var(--clr-gray-100)">
        <Table.Thead>
          <Table.Tr bg="var(--clr-gray-100)">
            <Table.Th>{t("patient")}</Table.Th>
            <Table.Th>{t("date")}</Table.Th>
            <Table.Th>{t("service")}</Table.Th>
            <Table.Th>{t("status")}</Table.Th>
            <Table.Th>{t("bill-status")}</Table.Th>
            <Table.Th>{t("doctor")}</Table.Th>
            {showCallMonitoring && <Table.Th>{t("call-monitoring")}</Table.Th>}
            <Table.Th ta="right">{t("last-updated-action")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {renderLoadingErrorData({
            isLoading: isLoading || isRefetching,
            isError,
            isSuccess,
            dataLength: data?.length || 0,
            emptyDataComponent: (
              <TableEmptyRow colSpan={columnCount} message={t("empty-schedule")} />
            ),
            errorComponent: (
              <TableErrorRow colSpan={columnCount} message={t("encountered-error")} />
            ),
            loadingComponent: (
              <TableSkeletonRows
                columns={columnCount}
                skeletonProps={{
                  widths: ["lg", "md", "lg", "md", "md", "lg", "md"],
                  rightAlignLastColumn: true,
                }}
              />
            ),
            dataComponent: scheduleRows,
          })}
        </Table.Tbody>
      </Table>
      <TablePagination
        paginationData={pagination}
        paginatedRows={data?.length || 0}
        onPageChange={onPageChange}
        isLoading={isLoading || isRefetching}
      />
    </>
  );
};
